import { QueryObserver, MutationObserver } from '@tanstack/query-core';
import { shallowEqual } from 'fast-equals';

export interface ApiViewState {
  errorLoading: Error | null;
  isLoading: boolean;
}

export const getDefaultApiViewState = (): ApiViewState => ({
  errorLoading: null,
  isLoading: true
});

export class ApiView {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private queryObservers: QueryObserver<any>[] = [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private mutationObservers: MutationObserver<any, any, any, any>[] = [];
  private unsubscribes: (() => void)[] = [];
  private _onChange: (() => void) | null = null;
  private hasSubscribedToObservers = false;

  public state: ApiViewState = getDefaultApiViewState();

  // ***************
  // *  Public API *
  // ***************

  public destroy() {
    this.unsubscribes.forEach(unsub => unsub());
    this.unsubscribes = [];
    this.hasSubscribedToObservers = false;
    this._onChange = null;
  }

  public get isLoading() {
    return this.queryObservers.some(obs => obs.getCurrentResult().isLoading);
  }

  public subscribe(onChange: () => void): void {
    this._onChange = onChange;
    this.subscribeToObservers();
  }

  public subscribeToObservers(): void {
    if (!this.hasSubscribedToObservers) {
      this.hasSubscribedToObservers = true;
      this.unsubscribes = [...this.queryObservers, ...this.mutationObservers].map(obs => {
        return obs.subscribe(() => this.notify());
      });

      this.notify();
    }
  }

  // ****************************
  // *  Private / Protected API *
  // ****************************
  protected addMutation<TData, TError, TVariables, TContext>(
    mutation: MutationObserver<TData, TError, TVariables, TContext>
  ): MutationObserver<TData, TError, TVariables, TContext> {
    this.mutationObservers.push(mutation);
    return mutation;
  }

  protected addQuery<T>(observer: QueryObserver<T>): QueryObserver<T> {
    this.queryObservers.push(observer);
    return observer;
  }

  protected addUnsubscribe(unsubscribe: () => void): void {
    this.unsubscribes.push(unsubscribe);
  }

  private getFirstLoadingError() {
    for (const obs of this.queryObservers) {
      const err = obs.getCurrentResult().error;
      if (err) {
        return err as Error;
      }
    }
    return null;
  }

  /** Will return a new state snapshot without modifying the current state. */
  protected getNewState(): ApiViewState {
    return {
      isLoading: this.isLoading,
      errorLoading: this.getFirstLoadingError()
    };
  }

  protected getResult<T>(observer: QueryObserver<T>) {
    return observer.getCurrentResult();
  }

  /** Call from subclass setters to notify subscribers of local state changes */
  protected notify() {
    if (this.updateState()) {
      this._onChange?.();
    }
  }

  protected updateDependentQueries() {
    // Override this in your view if you need to update any queries based on changes
    // to other queries or mutations. This will be called before getNewState() on
    // every notify() call, so you can ensure that the state snapshot is always up
    // to date with the latest query results.
  }

  protected updateState() {
    this.updateDependentQueries();
    const newState = this.getNewState();
    const hasChanged = !shallowEqual(this.state, newState);
    if (hasChanged) {
      this.state = newState;
    }
    return hasChanged;
  }
}
